if (!customElements.get('geolocation-popup')) {
  customElements.define(
    'geolocation-popup',
    class GeolocationPopup extends HTMLElement {
      constructor() {
        super();
        this.storageKey = 'ecobusters:geolocation-popup-dismissed';
      }

      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        if (Shopify.designMode) {
          const block = this.blocks[0];
          if (block) {
            this.activeBlock = block;
            this.activeLocale = (block.getAttribute('data-locale') || '').toLowerCase();
            this.show(block);
          }
          return;
        }

        if (!this.testMode) {
          try {
            if (window.localStorage.getItem(this.storageKey)) return;
          } catch (e) {
            // localStorage unavailable — keep going
          }
        }

        this.detectAndShow();
      }

      get testMode() {
        return this.getAttribute('data-test-mode') === 'true';
      }

      get delay() {
        return this.hasAttribute('data-delay') ? parseInt(this.getAttribute('data-delay'), 10) : 2;
      }

      get currentLocale() {
        return (this.getAttribute('data-current-locale') || '').toLowerCase();
      }

      get blocks() {
        return Array.from(this.querySelectorAll('[data-locale-block]'));
      }

      get form() {
        return this.querySelector('form[is="localization-listbox"]');
      }

      async detectAndShow() {
        const country = await this.detectCountry();
        let block = country ? this.findMatchingBlock(country) : null;

        if (!block && this.testMode) {
          block = this.blocks.find((b) => {
            const locale = (b.getAttribute('data-locale') || '').toLowerCase();
            return locale && locale !== this.currentLocale;
          }) || this.blocks[0];
        }

        if (!block) return;

        const targetLocale = (block.getAttribute('data-locale') || '').toLowerCase();
        if (!this.testMode && (!targetLocale || targetLocale === this.currentLocale)) return;

        this.activeBlock = block;
        this.activeLocale = targetLocale;

        setTimeout(() => this.show(block), this.delay * 1000);
      }

      async detectCountry() {
        try {
          const res = await fetch(`${Shopify.routes.root || '/'}browsing_context_suggestions.json`, {
            headers: { Accept: 'application/json' }
          });
          if (res.ok) {
            const data = await res.json();
            const handle = data?.detected_values?.country?.handle;
            if (handle) return handle.toUpperCase();
          }
        } catch (e) {
          // ignore, fall through to navigator.language
        }

        const navLang = (navigator.language || '').toLowerCase();
        const langToCountry = {
          nl: 'NL', de: 'DE', fr: 'FR', it: 'IT', es: 'ES', pt: 'PT', en: 'GB'
        };
        const primary = navLang.split('-')[0];
        return langToCountry[primary] || null;
      }

      findMatchingBlock(country) {
        const upper = country.toUpperCase();
        return this.blocks.find((block) => {
          const codes = (block.getAttribute('data-country-codes') || '')
            .split(',')
            .map((c) => c.trim().toUpperCase())
            .filter(Boolean);
          return codes.includes(upper);
        });
      }

      show(block) {
        this.blocks.forEach((b) => b.hidden = true);
        if (block) block.hidden = false;
        this.setAttribute('open', '');
        this.bindButtons();
      }

      hide() {
        this.removeAttribute('open');
      }

      dismiss() {
        try {
          window.localStorage.setItem(this.storageKey, '1');
        } catch (e) {
          // ignore
        }
        this.hide();
      }

      bindButtons() {
        if (this.buttonsBound) return;
        this.buttonsBound = true;

        this.querySelectorAll('[data-geolocation-accept]').forEach((btn) => {
          btn.addEventListener('click', this.onAccept.bind(this));
        });
        this.querySelectorAll('[data-geolocation-dismiss]').forEach((btn) => {
          btn.addEventListener('click', this.dismiss.bind(this));
        });
      }

      onAccept(event) {
        const locale = event.currentTarget.getAttribute('data-locale') || this.activeLocale;
        const form = this.form;
        if (!form || !locale) return;

        const localeInput = form.querySelector('input[name="locale_code"]');
        const returnTo = form.querySelector('input[name="return_to"]');
        if (localeInput) localeInput.value = locale;
        if (returnTo) returnTo.value = window.location.pathname + window.location.search;

        try {
          window.localStorage.setItem(this.storageKey, '1');
        } catch (e) {
          // ignore
        }

        form.submit();
      }
    }
  );
}
